import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomizedStatus from "../../Custom/CustomizedStatus";
import { IContactChannel } from "../../../types/global";
import { Box } from "@mui/material";
import CustomizedChips from "../../Custom/CustomizedChips";
import {
  businessTypeFormatter,
  contactChannelFormatter,
} from "../../../utils/Formatter/Global";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import {
  TagDocumentType,
  TagsFindManyByDocumentTypeQuery,
  useTagsFindManyByDocumentTypeQuery,
} from "../../../generated/general";

const useSupplierColumnDefs = () => {
  const { t } = useTranslation();
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

  const graphQLClient: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { refetch } =
    useTagsFindManyByDocumentTypeQuery<TagsFindManyByDocumentTypeQuery>(
      graphQLClient,
      {
        documentType: TagDocumentType.Contact,
      },
      {
        enabled: false,
      }
    );

  const contactChannelAction = (type: string, info: string) => {
    switch (type) {
      case "phone":
      case "fax":
        window.open(`tel:${info}`, "_self");
        return;
      case "email":
        window.open("mailto:" + info, "_self");
        return;
      case "line":
        window.open(
          "https://line.me/R/ti/p/" + info,
          "_blank",
          "noopener,noreferrer"
        );
        return;
      case "facebook":
        window.open(
          "https://facebook.com/" + info,
          "_blank",
          "noopener,noreferrer"
        );
        return;
      case "website":
        window.open(`https://${info}`, "_blank", "noopener,noreferrer");
        return;
      case "instagram":
        window.open(
          "https://instagram.com/" + info,
          "_blank",
          "noopener,noreferrer"
        );
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    setColumnDefs([
      {
        field: "unique_id",
        headerName: `รหัส${t(`contact.contact_type.supplier`)}`,
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "business_type",
        headerName: t("contact.info.business_type"),
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["individual", "corporate"],
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value) return businessTypeFormatter(params.value) || "-";
            else return "-";
          },
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return businessTypeFormatter(params.value) || "-";
          else return "-";
        },
        flex: 1,
        minWidth: 200,
      },
      {
        field: "full_name",
        headerName: `${t("contact.info.brand_name")}/ชื่อนามสกุล`,
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 440,
      },
      {
        field: "contact_channel_list",
        headerName: t("contact.contact_channel.index"),
        filter: false,
        cellRenderer: ({ value }: { value: IContactChannel[] }) => {
          if (
            value.length === 1 &&
            (!value[0].contact_channel_info ||
              value[0].contact_channel_info.trim().length === 0) &&
            (!value[0].contact_channel_type ||
              value[0].contact_channel_type.trim().length === 0)
          ) {
            return;
          } else {
            return value.map((channel, index) => (
              <Box
                sx={{ display: "inline-block", mr: 1 }}
                key={`${channel.contact_channel_info}-${index}`}
              >
                <CustomizedChips
                  color="primary"
                  variant="outlined"
                  onClick={() =>
                    contactChannelAction(
                      channel.contact_channel_type,
                      channel.contact_channel_info || "-"
                    )
                  }
                  label={`${contactChannelFormatter(
                    channel.contact_channel_type
                  )}: ${channel.contact_channel_info || "-"}`}
                  isRounded
                />
              </Box>
            ));
          }
        },
        flex: 1,
        minWidth: 200,
      },
      {
        field: "tag_list",
        headerName: t("contact.tag_list"),
        filter: "agSetColumnFilter",
        sortable: false,
        filterParams: {
          values: async (params: any) => {
            // fetch values from server
            const { data: tagListResult } = await refetch();
            const values = tagListResult?.TagsFindManyByDocumentType;
            const formatValues = values?.map((tag) => tag.name);
            params.success(formatValues);
          },
        },
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value)
            return params.value?.map((tag: string) => (
              <Box sx={{ display: "inline-block", mr: 1, gap: 1 }} key={tag}>
                <CustomizedChips
                  label={tag}
                  variant="outlined"
                  color="primary"
                  isRounded
                />
              </Box>
            ));
        },
        flex: 1,
        minWidth: 200,
      },
      {
        field: "status",
        headerName: t("status.index"),
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["1", "0"],
          valueFormatter: ({ value }: { value: string }) =>
            value === "1" ? "ใช้งาน" : "หยุดใช้งาน",
        },
        cellRenderer: ({ value }: { value: number }) => {
          return <CustomizedStatus status={value ? "active" : "inactive"} />;
        },
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      },
    ]);
  }, [refetch, t]);

  return columnDefs;
};

export default useSupplierColumnDefs;
